/**
 * Removes existing highlight spans and restores the original text nodes.
 * @param containerElement The element containing the highlighted spans.
 * @param className The class name of the highlight spans.
 */
const removeHighlights = (
  containerElement: HTMLElement,
  className: string,
): void => {
  const existingHighlights = containerElement.querySelectorAll(`.${className}`);
  existingHighlights.forEach((el: Element) => {
    const span = el as HTMLSpanElement;
    const parent = span.parentNode;
    if (parent) {
      parent.replaceChild(document.createTextNode(span.textContent || ''), span);
      // normalize() merges adjacent text nodes which might be created
      // after removing the span.
      parent.normalize();
    }
  });
};

/**
 * Finds a text node containing the specified sentence within an element,
 * wraps the sentence in a span with the given class name, and scrolls it into view.
 * @param containerElement The element to search within.
 * @param sentence The sentence text to find and highlight.
 * @param className The class name to apply to the highlight span.
 * @returns True if the sentence was found and highlighted, false otherwise.
 */
export const highlightAndScrollToSentenceInElement = (
  containerElement: HTMLElement | null,
  sentence: string | null,
  className = 'tts-active-sentence', // Default class name
): boolean => {
  if (!containerElement || !sentence) return false;

  // Remove previous highlights first
  removeHighlights(containerElement, className);

  // Use TreeWalker to efficiently find the text node containing the sentence
  const walker = document.createTreeWalker(
    containerElement,
    NodeFilter.SHOW_TEXT,
    null,
  );

  let node;
  while ((node = walker.nextNode())) {
    const nodeText = node.textContent || '';
    const sentenceIndex = nodeText.indexOf(sentence);

    if (sentenceIndex !== -1) {
      // Split the text node to insert the highlight span
      const beforeText = nodeText.substring(0, sentenceIndex);
      const afterText = nodeText.substring(sentenceIndex + sentence.length);

      const span = document.createElement('span');
      span.className = className;
      span.textContent = sentence;

      const fragment = document.createDocumentFragment();
      if (beforeText) fragment.appendChild(document.createTextNode(beforeText));
      fragment.appendChild(span);
      if (afterText) fragment.appendChild(document.createTextNode(afterText));

      // Replace the original text node with the fragment containing the span
      node.parentNode?.replaceChild(fragment, node);

      // Scroll the new span into view
      span.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return true; // Sentence found and highlighted
    }
  }

  return false; // Sentence not found
};
